import { logError } from "@/lib/log";
import { useEffect, useState, useCallback, useMemo } from "react";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { CategorySelect } from "@/components/categories/CategorySelect";
import { toast } from "sonner";
import { Sparkles, LineChart as LineIcon, PieChart as PieIcon, Plus, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Area, AreaChart } from "recharts";
import { Link } from "react-router-dom";
import { SpendingInsights } from "@/components/SpendingInsights";
import { SavingsGoalCard } from "@/components/SavingsGoalCard";
import { openVoiceExpense, onVoiceExpenseSaved } from "@/components/VoiceExpense";
import { Mic, AlertTriangle } from "lucide-react";

function dueLabel(days) {
  const n = Math.abs(days);
  const dayWord = `${n} day${n !== 1 ? "s" : ""}`;
  if (days < 0) return `overdue by ${dayWord}`;
  if (days === 0) return "due today";
  return `due in ${dayWord}`;
}

function aiButtonLabel(aiBusy, hasReport) {
  if (aiBusy) return "Consulting…";
  return hasReport ? "Refresh insights" : "Generate insights";
}

function BillsBanner({ bills }) {
  const now = new Date();
  const alerts = (bills || []).map((b) => {
    const days = Math.floor((new Date(b.due_date) - now) / 86400000);
    return { ...b, days };
  }).filter((b) => b.days <= 3);
  if (alerts.length === 0) return null;
  const overdue = alerts.filter((b) => b.days < 0);
  const urgent = overdue.length > 0;
  return (
    <div
      data-testid="bills-banner"
      className={`border p-4 flex items-start gap-3 ${urgent ? "border-[#D94F3D] bg-[#D94F3D]/5" : "border-[#A67C00] bg-[#A67C00]/5"}`}
    >
      <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${urgent ? "text-[#D94F3D]" : "text-[#A67C00]"}`} />
      <div className="text-sm leading-relaxed">
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase block mb-1" style={{ color: urgent ? "#D94F3D" : "#A67C00" }}>
          {urgent ? "Bills overdue" : "Bills due soon"}
        </span>
        {alerts.slice(0, 3).map((b) => (
          <span key={b.id} className="block">
            <strong>{b.name}</strong> — {formatINR(b.amount)}{" "}
            {dueLabel(b.days)}
          </span>
        ))}
        {alerts.length > 3 && <Link to="/bills" className="underline underline-offset-2 text-xs">+{alerts.length - 3} more →</Link>}
      </div>
    </div>
  );
}

const COLORS = ["#1A1F16", "#2E6F40", "#A67C00", "#D94F3D", "#6C7565", "#3C4537", "#8B7355"];
// Static chart configs hoisted to module scope so chart children don't re-render needlessly.
const TOOLTIP_STYLE = { background: "#FFF", border: "1px solid #D8D4C7", borderRadius: 0, fontSize: 12 };
const AXIS_LINE = { stroke: "#D8D4C7" };
const TREND_MARGIN = { top: 10, right: 12, left: 0, bottom: 0 };

const RANGES = [
  { k: "month", l: "This month" },
  { k: "3m", l: "3 months" },
  { k: "6m", l: "6 months" },
  { k: "ytd", l: "YTD" },
];

function Delta({ pct, invert = false }) {
  if (pct == null) return <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">—</span>;
  const positive = invert ? pct < 0 : pct >= 0;
  const Icon = pct >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-xs ${positive ? "text-[#2E6F40]" : "text-[#D94F3D]"}`}>
      <Icon className="w-3 h-3" /> {pct >= 0 ? "+" : ""}{pct}%
      <span className="text-[#6C7565] font-mono text-[10px] ml-1">vs prev</span>
    </span>
  );
}

function Sparkline({ data, dataKey, color }) {
  return (
    <div className="h-10">
      <ResponsiveContainer>
        <LineChart data={data}>
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiCard({ label, value, delta, deltaInvert, spark, sparkKey, sparkColor, testid }) {
  return (
    <div className="border border-[#D8D4C7] bg-white p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565]">{label}</div>
          <div className="font-serif text-4xl mt-2 num tracking-tight">{value}</div>
        </div>
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <Delta pct={delta} invert={deltaInvert} />
        {spark && <div className="w-24"><Sparkline data={spark} dataKey={sparkKey} color={sparkColor} /></div>}
      </div>
    </div>
  );
}

function QuickAdd({ onSaved }) {
  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Groceries");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!amount) return;
    setBusy(true);
    try {
      await api.post("/transactions", {
        type, amount: parseFloat(amount), category,
        description: "", date: new Date().toISOString(),
      });
      toast.success("Recorded");
      setAmount("");
      onSaved?.();
      // Notify all live-refresh subscribers (SavingsGoalCard, Transactions, etc.)
      window.dispatchEvent(new CustomEvent("voice-expense:saved"));
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };


  return (
    <form onSubmit={submit} className="border border-[#D8D4C7] bg-white p-6" data-testid="quick-add-widget">
      <div className="flex items-center gap-2 mb-4">
        <Plus className="w-4 h-4 text-[#6C7565]" />
        <h3 className="font-serif text-2xl tracking-tight">Quick log</h3>
      </div>
      <div className="flex gap-2 mb-3">
        {["expense", "income"].map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`quick-type-${t}`}
            onClick={() => { setType(t); setCategory(t === "expense" ? "Groceries" : "Salary"); }}
            className={`flex-1 py-2 text-[10px] uppercase tracking-widest border ${type === t ? "bg-[#1A1F16] text-[#F7F5F0] border-[#1A1F16]" : "border-[#1A1F16]"}`}
          >{t}</button>
        ))}
      </div>
      <input
        type="number"
        step="0.01"
        placeholder="₹ 0"
        required
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        data-testid="quick-amount-input"
        className="w-full bg-transparent border-b border-[#1A1F16] py-2 font-serif text-2xl num focus:outline-none focus:border-[#A67C00] mb-3"
      />
      <CategorySelect
        type={type}
        value={category}
        onChange={setCategory}
        testid="quick-category-select"
        className="w-full bg-transparent border-b border-[#1A1F16] py-2 mb-4 focus:outline-none"
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          data-testid="quick-submit-btn"
          className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#2C3628] disabled:opacity-50"
        >
          {busy ? "Logging…" : "Record"}
        </button>
        <button
          type="button"
          data-testid="quick-voice-btn"
          onClick={openVoiceExpense}
          title="Log by voice"
          className="border border-[#1A1F16] px-3 hover:bg-[#1A1F16] hover:text-[#F7F5F0]"
        >
          <Mic className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}

function Gauge({ value }) {
  const clamped = Math.max(0, Math.min(100, value));
  const angle = (clamped / 100) * 180;
  const good = clamped >= 20;
  return (
    <div className="relative w-full max-w-[220px] mx-auto" data-testid="savings-gauge">
      <svg viewBox="0 0 200 110" className="w-full">
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#EAE7DD" strokeWidth="14" strokeLinecap="round" />
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke={good ? "#2E6F40" : "#D94F3D"}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${(angle / 180) * 251} 251`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
        <div className={`font-serif text-4xl num ${good ? "text-[#1A1F16]" : "text-[#D94F3D]"}`}>{clamped.toFixed(0)}%</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Savings rate</div>
      </div>
    </div>
  );
}

function Masthead({ range, setRange }) {
  const rangeLabel = RANGES.find((r) => r.k === range)?.l || "";
  return (
    <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6 flex-wrap gap-4">
      <div>
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">The Daily Statement · {rangeLabel}</span>
        <h2 className="font-serif text-5xl lg:text-6xl tracking-tight mt-2">Overview.</h2>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex border border-[#1A1F16]">
          {RANGES.map((r) => (
            <button
              key={r.k}
              data-testid={`range-${r.k}`}
              onClick={() => setRange(r.k)}
              className={`px-3 py-2 text-[10px] uppercase tracking-widest ${range === r.k ? "bg-[#1A1F16] text-[#F7F5F0]" : "text-[#1A1F16] hover:bg-[#EAE7DD]"}`}
            >{r.l}</button>
          ))}
        </div>
        <div className="hidden md:block font-mono text-[10px] uppercase tracking-widest text-[#6C7565] text-right">
          As of<br/>
          <span className="text-[#1A1F16] text-sm normal-case font-sans">{new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</span>
        </div>
      </div>
    </div>
  );
}

function KpiRow({ data }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#D8D4C7] border border-[#D8D4C7]">
      <KpiCard label="Net Worth" value={formatINR(data.net_worth)} testid="stat-net-worth" />
      <KpiCard label="Income" value={formatINR(data.income_month)} delta={data.income_delta_pct} spark={data.trend} sparkKey="income" sparkColor="#2E6F40" testid="stat-income" />
      <KpiCard label="Expense" value={formatINR(data.expense_month)} delta={data.expense_delta_pct} deltaInvert spark={data.trend} sparkKey="expense" sparkColor="#D94F3D" testid="stat-expense" />
      <KpiCard label="Savings" value={formatINR(data.savings_month)} delta={data.savings_delta_pct} spark={data.trend} sparkKey="savings" sparkColor="#1A1F16" testid="stat-savings" />
    </div>
  );
}

function TrendSection({ data, onQuickSaved }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
      <div className="lg:col-span-4 border border-[#D8D4C7] bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565]">Six-month flow</div>
            <h3 className="font-serif text-2xl mt-1">Income vs Expense</h3>
          </div>
          <LineIcon className="w-4 h-4 text-[#6C7565]" />
        </div>
        <div className="h-72" data-testid="chart-trend">
          <ResponsiveContainer>
            <AreaChart data={data.trend} margin={TREND_MARGIN}>
              <CartesianGrid stroke="#EAE7DD" vertical={false} />
              <defs>
                <linearGradient id="dashInc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2E6F40" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2E6F40" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="dashExp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D94F3D" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#D94F3D" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" stroke="#6C7565" fontSize={11} tickLine={false} axisLine={AXIS_LINE} />
              <YAxis stroke="#6C7565" fontSize={11} tickLine={false} axisLine={AXIS_LINE} tickFormatter={(v) => formatINR(v, { compact: true })} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => formatINR(v)} />
              <Area type="monotone" dataKey="income" stroke="#2E6F40" strokeWidth={2} fill="url(#dashInc)" />
              <Area type="monotone" dataKey="expense" stroke="#D94F3D" strokeWidth={2} fill="url(#dashExp)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="lg:col-span-2 grid grid-rows-2 gap-6">
        <div className="border border-[#D8D4C7] bg-white p-6 flex flex-col justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565]">This period</div>
            <h3 className="font-serif text-2xl">You keep</h3>
          </div>
          <Gauge value={data.savings_rate || 0} />
        </div>
        <QuickAdd onSaved={onQuickSaved} />
      </div>
    </div>
  );
}

function CategoryDonut({ categories }) {
  return (
    <div className="lg:col-span-2 border border-[#D8D4C7] bg-white p-6">
      <div className="flex items-center gap-2 mb-4">
        <PieIcon className="w-4 h-4 text-[#6C7565]" />
        <h3 className="font-serif text-2xl tracking-tight">Where rupees went</h3>
      </div>
      <div className="h-72" data-testid="chart-categories">
        {categories.length === 0 ? (
          <div className="h-full flex items-center justify-center text-[#6C7565] text-sm">No expenses recorded yet.</div>
        ) : (
          <ResponsiveContainer>
            <PieChart>
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => formatINR(v)} />
              <Pie data={categories.slice(0, 6)} dataKey="amount" nameKey="category" innerRadius={55} outerRadius={100} paddingAngle={2}>
                {categories.slice(0, 6).map((c, i) => <Cell key={c.category} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
      <ul className="mt-2 space-y-1.5">
        {categories.slice(0, 6).map((c, i) => (
          <li key={c.category} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5" style={{ background: COLORS[i % COLORS.length] }} />
              <span className="font-mono uppercase tracking-wider text-[#6C7565]">{c.category}</span>
            </span>
            <span className="num">{formatINR(c.amount, { compact: true })}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecentEntries({ recent }) {
  return (
    <div className="lg:col-span-3 border border-[#D8D4C7] bg-white p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-2xl tracking-tight">Recent entries</h3>
        <Link to="/transactions" className="text-[10px] uppercase tracking-widest text-[#6C7565] hover:text-[#1A1F16] underline underline-offset-4" data-testid="see-all-transactions">
          See all →
        </Link>
      </div>
      <div className="overflow-x-auto" data-testid="recent-transactions-table">
        {recent.length === 0 ? (
          <div className="text-sm text-[#6C7565] italic py-6">No entries yet.</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {recent.slice(0, 8).map((t) => (
                <tr key={t.id} className="border-b border-[#EAE7DD] last:border-0">
                  <td className="py-2.5 num text-[#6C7565] w-24">{formatDate(t.date)}</td>
                  <td className="py-2.5 pr-3">
                    <div className="font-sans">{t.description || t.category}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">{t.category}</div>
                  </td>
                  <td className={`py-2.5 text-right num font-medium ${t.type === "income" ? "text-[#2E6F40]" : "text-[#D94F3D]"}`}>
                    {t.type === "income" ? "+" : "−"} {formatINR(t.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AdvisorCard({ ai, aiBusy, aiLines, onGenerate }) {
  return (
    <div className="border border-[#D8D4C7] bg-[#FDFBF7] p-6 lg:p-8 relative paper-grain" data-testid="ai-insights-card">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#A67C00]" />
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#A67C00]">
            <Sparkles className="w-3.5 h-3.5" /> GPT-5.4 Mini Intelligence
          </div>
          <h3 className="font-serif text-3xl lg:text-4xl mt-2 tracking-tight">A few words from your advisor.</h3>
        </div>
        <button
          data-testid="ai-refresh-btn"
          onClick={onGenerate}
          disabled={aiBusy}
          className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] disabled:opacity-50"
        >
          {aiButtonLabel(aiBusy, Boolean(ai))}
        </button>
      </div>
      <div className="mt-6 max-w-3xl">
        {ai ? (
          <div className="font-serif text-lg lg:text-xl leading-relaxed text-[#1A1F16] space-y-3" data-testid="ai-insights-text">
            {aiLines.map((line, i) => (
              <p key={`line-${i}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-sans uppercase tracking-wider text-sm text-[#A67C00] mr-2">$1</strong>')) }} />
            ))}
          </div>
        ) : (
          <p className="text-[#6C7565] italic font-serif text-lg">Generate a personalised brief based on your latest activity. Powered by GPT-5.4 Mini.</p>
        )}
      </div>
    </div>
  );
}

function BillsAndBudgets({ data }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-[#D8D4C7] bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl tracking-tight">Upcoming bills</h3>
          <Link to="/bills" className="text-[10px] uppercase tracking-widest text-[#6C7565] hover:text-[#1A1F16] underline underline-offset-4">All bills →</Link>
        </div>
        {data.upcoming_bills.length === 0 ? (
          <div className="text-sm text-[#6C7565] py-4">No bills scheduled.</div>
        ) : (
          <ul className="divide-y divide-[#D8D4C7]" data-testid="upcoming-bills-list">
            {data.upcoming_bills.slice(0, 6).map((b) => (
              <li key={b.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-sans text-sm">{b.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">{b.category} · Due {new Date(b.due_date).toLocaleDateString("en-IN")}</div>
                </div>
                <div className="num font-serif text-xl">{formatINR(b.amount)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border border-[#D8D4C7] bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl tracking-tight">Budget pulse</h3>
          <Link to="/budgets" className="text-[10px] uppercase tracking-widest text-[#6C7565] hover:text-[#1A1F16] underline underline-offset-4">All budgets →</Link>
        </div>
        {data.budgets.length === 0 ? (
          <div className="text-sm text-[#6C7565]">No budgets yet.</div>
        ) : (
          <ul className="space-y-4" data-testid="budget-pulse">
            {data.budgets.slice(0, 5).map((b) => {
              const spent = (data.categories.find((c) => c.category === b.category)?.amount) || 0;
              const pct = Math.min(100, Math.round((spent / (b.amount || 1)) * 100));
              const over = spent > b.amount;
              return (
                <li key={b.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="uppercase tracking-wider text-[10px] text-[#6C7565] font-mono">{b.category}</span>
                    <span className="num text-xs">{formatINR(spent, { compact: true })} / {formatINR(b.amount, { compact: true })}</span>
                  </div>
                  <div className="h-1.5 bg-[#EAE7DD]">
                    <div className={`h-full ${over ? "bg-[#D94F3D]" : "bg-[#2E6F40]"}`} style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [range, setRange] = useState("month");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/summary?range=${range}`);
      setData(data);
    } catch (e) {
      logError("Summary load failed", e);
      toast.error("Failed to load summary");
    }
  }, [range]);

  const fetchAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/insights");
      setAi(data.insights);
    } catch { toast.error("AI insights failed"); }
    finally { setAiBusy(false); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => onVoiceExpenseSaved(() => load()), [load]);

  const aiLines = useMemo(() => (ai ? ai.split("\n").filter(Boolean) : []), [ai]);

  if (!data) return <div className="text-[#6C7565] font-mono text-xs uppercase tracking-widest">Reading the books…</div>;

  return (
    <div className="space-y-10">
      <Masthead range={range} setRange={setRange} />
      <BillsBanner bills={data.upcoming_bills} />
      <KpiRow data={data} />
      <SavingsGoalCard />
      <SpendingInsights variant="cards" kicker="GPT-5.4 Mini · Auto-analysis" heading="Spending signals." testid="spending-signals" />
      <TrendSection data={data} onQuickSaved={() => load()} />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <CategoryDonut categories={data.categories} />
        <RecentEntries recent={data.recent} />
      </div>
      <AdvisorCard ai={ai} aiBusy={aiBusy} aiLines={aiLines} onGenerate={fetchAi} />
      <BillsAndBudgets data={data} />
    </div>
  );
}
